import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, EyeOff, Radio } from 'lucide-react';
import { Button } from '../../shared/ui/Button.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { useGuidanceActions, useJourneyActions, useJourneyGraph } from '../../shared/api/queries.js';
import { JourneyReading } from './components/JourneyReading.js';
import { StepDetailPanel } from './components/StepDetailPanel.js';
import './journey-detail.css';

const LANGUAGES = ['fr', 'ar'] as const;

const plural = (value: number, singular: string, pluralForm: string) => (value > 1 ? pluralForm : singular);

/**
 * The "Parcours utilisateur" tab: the reading (flow or graph) and the pages on the left, the
 * selected step's measurement and its generated guidance on the right.
 * Publishing a guidance is one operation from its card: the configuration
 * release follows.
 */
export function JourneyDetailPage() {
  const { journeyId } = useParams<{ journeyId: string }>();
  const { data: graph, isLoading, isError } = useJourneyGraph(journeyId);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const actions = useGuidanceActions();
  const journey = useJourneyActions(journeyId);

  if (isLoading) return <p className="journey__loading">Chargement du parcours…</p>;
  if (isError) return <p className="journey__loading">Ce parcours n'a pas pu être chargé. Réessayez.</p>;
  if (!graph) return <p className="journey__loading">Ce parcours n'a pas encore été analysé.</p>;

  const selectedStep = graph.steps.find((step) => step.id === selectedStepId) ?? null;
  const stepCount = graph.steps.length;

  return (
    <div className="journey">
      <PageHeader
        title={graph.journey.name}
        description={`Parcours reconstitué à partir de la mesure · ${graph.journey.sessionCount.toLocaleString('fr-FR')} ${plural(
          graph.journey.sessionCount,
          'usager',
          'usagers',
        )} · ${stepCount} ${plural(stepCount, 'page', 'pages')}`}
        actions={
          <>
            <Link to="/journeys">
              <Button variant="ghost" icon={<ArrowLeft />}>
                Tous les parcours
              </Button>
            </Link>
            <Button
              variant="secondary"
              icon={graph.journey.tracked ? <EyeOff /> : <Radio />}
              title={
                graph.journey.tracked
                  ? "Ce parcours est surveillé en priorité par l'équipe."
                  : "Marquer ce parcours comme surveillé en priorité par l'équipe."
              }
              onClick={() => journey.setTracked.mutate(!graph.journey.tracked)}
            >
              {graph.journey.tracked ? 'Ne plus suivre' : 'Suivre ce parcours'}
            </Button>
          </>
        }
      />

      <div className="journey__layout">
        <div className="journey__main">
          <JourneyReading graph={graph} selectedStepId={selectedStepId} onSelectStep={setSelectedStepId} />
        </div>
        <StepDetailPanel
          step={selectedStep}
          steps={graph.steps}
          guidances={graph.guidances}
          languages={LANGUAGES}
          actions={actions}
          onRenameStep={(name) =>
            selectedStep && journey.renameStep.mutate({ stepId: selectedStep.id, name })
          }
        />
      </div>
    </div>
  );
}
